import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import axios from "axios";

// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";
import HaltReportTable from "./haltReportTable";
import { haltreportendpoint } from "config";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange";
import DhundhooLoader from "../Loader/Loader";

function HaltReport() {
  // Toggle State
  const [viewType, setViewType] = useState("report");

  // Data States
  const [haltData, setHaltData] = useState([]);
  const [tripData, setTripData] = useState([]);

  // UI States
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [interval, setInterval] = useState("None");

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  const renderMapLink = (lat, lng, label, colorClass) => (
    <MDBox display="flex" alignItems="center">
      <LocationOnIcon fontSize="small" sx={{ color: `${colorClass}.main`, mr: 0.5 }} />
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1a73e8", fontSize: "0.85rem", fontWeight: "bold", textDecoration: "underline" }}
      >
        {label}
      </a>
    </MDBox>
  );

  // ✅ FIXED: Strictly turned off sorting for all metadata metrics columns using both flags
  const reportColumns = [
    { Header: "Thing Name", accessor: "thingName", width: "15%", disableSortBy: true, canSort: false },
    { Header: "Time Calculate", accessor: "timeCalculate", width: "15%" }, // ONLY THIS WILL SORT NOW
    { Header: "Time (Min)", accessor: "timeInMinutes", width: "10%", disableSortBy: true, canSort: false },
    { Header: "Start Time", accessor: "haltStartTimeDate", width: "20%", disableSortBy: true, canSort: false },
    { Header: "End Time", accessor: "haltEndTimeDate", width: "20%", disableSortBy: true, canSort: false },
    { Header: "Location Pin", accessor: "locationPin", width: "20%", disableSortBy: true, canSort: false },
  ];

  const tripColumns = [
    { Header: "Trip ID", accessor: "id", width: "10%", disableSortBy: true, canSort: false },
    { Header: "Start Time", accessor: "startTime", width: "20%", disableSortBy: true, canSort: false },
    { Header: "Start Location", accessor: "startLocation", width: "25%", disableSortBy: true, canSort: false },
    { Header: "End Time", accessor: "endTime", width: "20%", disableSortBy: true, canSort: false },
    { Header: "End Location", accessor: "endLocation", width: "25%", disableSortBy: true, canSort: false },
  ];

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const [d, t] = isoString.split("T");
    return `${d.replace(/-/g, "/")} ${t}`;
  };

  const formatDateOnly = (isoString) => {
    if (!isoString) return "";
    const [d] = isoString.split("T");
    return d.replace(/-/g, "/");
  };

  const fetchReportLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const data = JSON.stringify({
        date: null,
        zone: "Asia/Kolkata",
        things: vehicleId,
        startTime: formatDateTime(startTime),
        endTime: formatDateTime(endTime),
        interval: interval === "None" ? null : interval,
        thingIds: [],
        geofence: [],
        groups: [],
        orgName: "",
        geoFenceName: "",
        geoLat: 0,
        geoLong: 0
      });

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: haltreportendpoint,
        headers: { "x-dhundhoo-session": token, "Content-Type": "application/json" },
        data,
      };

      const response = await axios.request(config);

      const formattedRows = response.data.map((log) => {
        const lat = log.haltLatitude !== undefined ? log.haltLatitude : (log.lat || "");
        const lng = log.haltLongitude !== undefined ? log.haltLongitude : (log.lng || "");

        return {
          timeCalculate: log.timeCalculate,
          timeInMinutes: log.timeInMinutes,
          thingName: log.thingName,
          haltStartTimeDate: log.haltStartTimeDate,
          haltEndTimeDate: log.haltEndTimeDate,
          locationPin: (lat && lng) ? renderMapLink(lat, lng, "View Location", "info") : "N/A",
          rawLat: lat,
          rawLng: lng
        };
      });

      setHaltData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading halt report");
      openErrorSB();
    }
  };

  const fetchTripLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const data = JSON.stringify({
        date: formatDateOnly(startTime),
        zone: "Asia/Kolkata",
        startTime: formatDateTime(startTime),
        endTime: formatDateTime(endTime),
        things: vehicleId,
        thingIds: [],
        geofence: [],
        groups: [],
        interval: interval === "None" ? 0 : Number(interval),
        orgName: "",
        geoFenceName: "",
        geoLat: 0,
        geoLong: 0
      });

      const config = {
        method: "post",
        url: "https://v3.dhundhoo.com/reports/haltTripReport",
        headers: { "x-dhundhoo-session": token, "Content-Type": "application/json" },
        data,
      };

      const response = await axios.request(config);
      const resData = response.data || {};

      const formattedTrips = Object.keys(resData).map((key) => {
        const trip = resData[key];
        return {
          id: key,
          startTime: trip.startTime,
          endTime: trip.endTime,
          rawStartLat: trip.startLatitude,
          rawStartLng: trip.startLongitude,
          startLocation: renderMapLink(trip.startLatitude, trip.startLongitude, "View Start", "info"),
          rawEndLat: trip.endLatitude,
          rawEndLng: trip.endLongitude,
          endLocation: renderMapLink(trip.endLatitude, trip.endLongitude, "View End", "error"),
        };
      });

      setTripData(formattedTrips);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading halt trip report");
      openErrorSB();
    }
  };

  const handleLoadData = async () => {
    if (!vehicleId) {
      setErrorMessage("Please select a vehicle first");
      openErrorSB();
      return;
    }

    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first");
      openErrorSB();
      return;
    }

    setLoading(true);
    setErrorMessage("");

    if (viewType === "report") {
      await fetchReportLogs();
    } else {
      await fetchTripLogs();
    }

    setLoading(false);
  };

  const exportToCSV = () => {
    let headers = [];
    let rows = [];
    let fileName = "";

    const formatFileDate = (dateTime) => {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formattedDate = `${formatFileDate(startTime)}_to_${formatFileDate(endTime)}`;

    if (viewType === "report") {
      if (haltData.length === 0) return handleNoDataExport();
      headers = ["Thing Name", "Time Calculate", "Time In Minutes", "Halt Start Time", "Halt End Time", "Latitude", "Longitude"];
      rows = haltData.map((row) => [
        row.thingName, row.timeCalculate, row.timeInMinutes, row.haltStartTimeDate, row.haltEndTimeDate, row.rawLat, row.rawLng
      ].join(","));
      fileName = `HaltReport_${formattedDate}.csv`;
    } else {
      if (tripData.length === 0) return handleNoDataExport();
      headers = ["Trip ID", "Start Time", "Start Lat", "Start Long", "End Time", "End Lat", "End Long"];
      rows = tripData.map((row) => [
        row.id, row.startTime, row.rawStartLat, row.rawStartLng, row.endTime, row.rawEndLat, row.rawEndLng
      ].join(","));
      fileName = `HaltTripReport_${formattedDate}.csv`;
    }

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNoDataExport = () => {
    setErrorMessage("No data to export");
    openErrorSB();
  };

  const handleClear = () => {
    setVehicleId("");
    setInterval("None");
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setHaltData([]);
    setTripData([]);
    setErrorMessage("");
  };

  const hasData = viewType === "report" ? haltData.length > 0 : tripData.length > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0} display="flex" justifyContent="space-between" alignItems="center">
                <ToggleButtonGroup
                  color="info"
                  value={viewType}
                  exclusive
                  onChange={(e, newView) => {
                    if (newView !== null) {
                      setViewType(newView);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="report">Halt Report</ToggleButton>
                  <ToggleButton value="trip">Halt Trip</ToggleButton>
                </ToggleButtonGroup>
              </MDBox>

              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        disablePortal
                        options={things || []}
                        getOptionLabel={(option) => option.thingId || ""}
                        filterOptions={(options, { inputValue }) => {
                          const searchValue = inputValue.toLowerCase().trim();

                          return options.filter((option) => {
                            const name = (option.name || "").toLowerCase();
                            const nickName = (option.nickName || "").toLowerCase();
                            const thingId = (option.thingId || "").toLowerCase();

                            return (
                              name.includes(searchValue) ||
                              nickName.includes(searchValue) ||
                              thingId.includes(searchValue)
                            );
                          });
                        }}
                        value={things?.find((t) => t.thingId === vehicleId) || null}
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              fontSize: "0.85rem",
                            }}
                          >
                            <strong>{option.nickName || option.name || "-"}</strong>
                            <span style={{ color: "#666", marginLeft: "10px" }}>
                              {option.thingId}
                            </span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Vehicle"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#FB8C00",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FB8C00",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={["None", 1, 3, 5, 7, 9]}
                        value={interval}
                        onChange={(e, newValue) => setInterval(newValue)}
                        renderInput={(params) => <TextField {...params} label="Interval (sec)" variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FB8C00",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FB8C00",
                            },
                          }}
                        />}
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <div style={{ width: '56%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <MDButton fullWidth variant="gradient" color="warning" size="small" onClick={handleLoadData} disabled={loading}>
                            {loading ? <CircularProgress size={15} color="inherit" /> : "LOAD"}
                          </MDButton>
                        </Grid>
                        <Grid item xs={4}>
                          <MDButton fullWidth variant="outlined" color="error" size="small" onClick={handleClear} disabled={loading}>
                            CLEAR
                          </MDButton>
                        </Grid>
                        <Grid item xs={4}>
                          <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || !hasData} />
                        </Grid>
                      </Grid>
                    </Grid>

                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </MDBox>
              )}

              {/* ✅ FIXED 2: Added absolute styling to enforce wrapper local scrolling */}
              <MDBox
                pt={3}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
                sx={{
                  maxHeight: {
                    xs: "calc(100vh - 430px)", // phones
                    sm: "calc(100vh - 400px)", // tablets
                    md: "calc(100vh - 280px)", // laptops
                    lg: "calc(100vh - 200px)", // desktops / large screens
                  },
                  minHeight: "200px", // taaki chhoti screen pe bhi bahut zyada shrink na ho
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: "4px" }
                }}
              >
                {loading ? <DhundhooLoader /> : (
                  <HaltReportTable
                    table={{
                      columns: viewType === "report" ? reportColumns : tripColumns,
                      rows: viewType === "report" ? haltData : tripData
                    }}
                    isSorted={true}
                    entriesPerPage={false} // Removed pagination dropdown
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar color="success" icon="check" title="Success" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
      <MDSnackbar color="error" icon="warning" title="Error" content={errorMessage || "Failed to load logs."} open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
    </DashboardLayout>
  );
}

export default HaltReport;